
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

export default function PricingPreview() {
  const headingAnim = useScrollAnimation({ threshold: 0.2 });
  const cardsAnim = useStaggerAnimation(3, { threshold: 0.1 });
  const ctaAnim = useScrollAnimation({ threshold: 0.3 });

  const tiers = [
    {
      name: 'Website Build',
      price: '$1,500',
      period: 'one-time',
      features: [
        'Custom design',
        'Mobile-optimized',
        'SEO-ready structure',
      ],
    },
    {
      name: 'Maintenance',
      price: '$125',
      period: '/month',
      features: [
        'Hosting & security',
        'Content updates',
        'Performance monitoring',
      ],
      highlighted: true,
    },
    {
      name: 'Local Visibility',
      price: '$275',
      period: '/month',
      features: [
        'Local SEO optimization',
        'Google Business setup',
        'Monthly reporting',
      ],
    },
  ];

  return (
    <section className="py-28 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div
          ref={headingAnim.ref}
          className="text-center mb-16"
          style={{
            opacity: headingAnim.isVisible ? 1 : 0,
            transform: headingAnim.isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <span className="font-inter font-semibold text-xs tracking-widest uppercase text-primary/60 mb-3 block">Pricing</span>
          <h2 className="font-sora font-bold text-4xl lg:text-[44px] text-charcoal mb-4 leading-tight">
            Transparent pricing
          </h2>
          <p className="font-inter text-base text-gray-500 max-w-md mx-auto">
            No surprises. Just clear, honest pricing for every stage of your growth.
          </p>
        </div>

        <div ref={cardsAnim.ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {tiers.map((tier, index) => (
            <div
              key={index}
              className={`rounded-2xl p-8 transition-all duration-300 ${
                tier.highlighted
                  ? 'bg-charcoal text-white ring-1 ring-charcoal lg:-translate-y-3 shadow-xl'
                  : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
              style={cardsAnim.getItemStyle(index)}
            >
              <p className={`font-inter font-semibold text-sm mb-5 ${tier.highlighted ? 'text-white/60' : 'text-gray-400'}`}>
                {tier.name}
              </p>
              <div className="flex items-baseline gap-1 mb-6">
                <span className={`font-sora font-bold text-4xl ${tier.highlighted ? 'text-white' : 'text-charcoal'}`}>
                  {tier.price}
                </span>
                <span className={`font-inter text-sm ${tier.highlighted ? 'text-white/50' : 'text-gray-400'}`}>
                  {tier.period}
                </span>
              </div>

              <div className={`border-t pt-6 mb-2 ${tier.highlighted ? 'border-white/15' : 'border-gray-100'}`}>
                <ul className="space-y-3.5">
                  {tier.features.map((feature, idx) => (
                    <li
                      key={idx}
                      className={`font-inter text-sm flex items-center gap-2.5 ${tier.highlighted ? 'text-white/80' : 'text-gray-600'}`}
                    >
                      <i className={`ri-check-line text-base ${tier.highlighted ? 'text-emerald-400' : 'text-emerald-500'}`}></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div
          ref={ctaAnim.ref}
          className="text-center"
          style={{
            opacity: ctaAnim.isVisible ? 1 : 0,
            transform: ctaAnim.isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-charcoal text-white px-8 py-3.5 rounded-xl font-inter font-medium text-sm hover:bg-primary transition-colors whitespace-nowrap cursor-pointer"
          >
            View Full Pricing
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
